import { useLang } from "@/context/LanguageContext";
import { ArrowDown } from "lucide-react";
import BrandMarkWatermark from "@/components/BrandMarkWatermark";

const HERO_IMG = "https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920";

export default function Hero() {
  const { t, isRtl } = useLang();

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const stats = [t.hero.stat_1, t.hero.stat_2, t.hero.stat_3, t.hero.stat_4];

  return (
    <section id="top" className="hero-frame min-h-[100svh] flex flex-col" data-testid="hero-section">
      <div className="hero-image-wrap">
        <img
          src={HERO_IMG}
          alt="Architectural detail"
          className="hero-image animate-slow-zoom"
          loading="eager"
        />
        <div className="hero-overlay" />
      </div>

      <div className="relative z-10 flex-1 flex items-end pt-40 pb-16">
        <BrandMarkWatermark
          size={900}
          tone="dark"
          opacity={0.11}
          style={{ top: 20, right: -100, zIndex: 1 }}
        />
        <div className="container-lux w-full relative z-10">
          <div className="max-w-3xl">
            <div className="overline animate-fade-up" data-testid="hero-overline">{t.hero.overline}</div>

            <h1
              className="mt-6 font-serif-display text-[clamp(2.75rem,7vw,6rem)] leading-[1.02] tracking-[-0.015em] animate-fade-up"
              data-testid="hero-title"
              style={{ animationDelay: "0.1s" }}
            >
              <span className="block text-[color:var(--anthracite)]">{t.hero.title_a}</span>
              <span className="block text-[color:var(--copper)] italic" style={{ fontWeight: 400 }}>
                {t.hero.title_b}
              </span>
            </h1>

            <p
              className="mt-8 max-w-xl text-[17px] leading-[1.7] text-[color:var(--anthracite)]/75 animate-fade-up"
              style={{ animationDelay: "0.25s" }}
              data-testid="hero-lede"
            >
              {t.hero.lede}
            </p>

            <div
              className="mt-12 flex flex-wrap items-center gap-4 animate-fade-up"
              style={{ animationDelay: "0.4s" }}
            >
              <button
                onClick={() => scrollTo("#contact")}
                className="btn-primary"
                data-testid="hero-primary-cta"
              >
                {t.hero.primary_cta}
                <span aria-hidden>{isRtl ? "←" : "→"}</span>
              </button>
              <button
                onClick={() => scrollTo("#projects")}
                className="btn-secondary"
                data-testid="hero-secondary-cta"
              >
                {t.hero.secondary_cta}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 border-t border-[color:var(--line)] bg-[color:var(--bone)]/70 backdrop-blur-md">
        <div className="container-lux">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.map((s, i) => (
              <div key={s.label} className="stat-block" data-testid={`hero-stat-${i}`}>
                <div className="stat-value font-serif-display">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={() => scrollTo("#about")}
        aria-label="Scroll"
        className="hidden md:flex absolute z-10 bottom-32 end-8 flex-col items-center gap-2 text-[color:var(--anthracite)]/60 hover:text-[color:var(--copper)] transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] tracking-[0.28em] uppercase">Scroll</span>
        <ArrowDown size={16} strokeWidth={1} className="animate-bounce" />
      </button>
    </section>
  );
}
